/**
 * 회원가입 스텝 추적 훅
 * 
 * 회원가입 프로세스의 각 단계를 추적
 * - signup_step_view: 스텝 진입
 * - signup_step_submit: 스텝 완료 (성공/실패)
 * 
 * ⚠️ intent_id는 내부적으로 auth 의도의 intent_id를 세션 스토리지에서 자동으로 참조합니다.
 * 
 * @example
 * ```tsx
 * // 회원가입 페이지
 * function SignupPage() {
 *   const signupStep = useSignupStep();
 *   const [currentStep, setCurrentStep] = useState<SignupStepKey>('credential_verify');
 *   
 *   useEffect(() => {
 *     // 스텝 진입 (intent_id는 auth에서 자동으로 가져옴)
 *     signupStep.view({
 *       step_key: currentStep,
 *       step_index: 1,
 *     });
 *   }, [currentStep]);
 *   
 *   const handleSubmitStep = async (data: any) => {
 *     try {
 *       await apiValidateStep(currentStep, data);
 *       
 *       // 스텝 완료 (성공)
 *       signupStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'ok',
 *       });
 *       
 *       setCurrentStep(getNextStep(currentStep));
 *     } catch (error) {
 *       // 스텝 완료 (실패)
 *       signupStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'fail',
 *         fail_reason: error.code === 'DUPLICATE' ? 'duplicate' : 'validation_error',
 *       });
 *     }
 *   };
 * }
 * ```
 */

import { useCallback, useRef } from 'react';
import { ulid } from 'ulid';
import { track } from '../../../core/client';
import { getIntentId } from '../../../core/ids';

export type SignupStepKey =
    | 'credential_verify'  // 이메일/password 검증
    | 'nickname_set'       // 닉네임 설정
    | 'phone_verify'       // 휴대폰 인증
    | 'tos_consent'        // 약관 동의
    | 'asset_profile';     // 추가 정보 (선택)

export interface SignupStepViewParams {
    step_key: SignupStepKey;
    step_index: number;
}

export interface SignupStepSubmitParams {
    step_key: SignupStepKey;
    step_index: number;
    outcome: 'ok' | 'fail';
    fail_reason?: string;
}

export interface SignupStepActions {
    view: (params: SignupStepViewParams) => void;
    submit: (params: SignupStepSubmitParams) => void;
}

/**
 * 회원가입 스텝 추적 훅
 */
export function useSignupStep(): SignupStepActions {


    /**
     * auth 의도의 intent_id를 세션 스토리지에서 가져오기
     * 없으면 fallback으로 새로 생성
     */
    const getAuthIntentId = useCallback((): string => {
        const authIntent = getIntentId('auth');
        if (authIntent?.intentId) {
            return authIntent.intentId;
        }
        // fallback: auth가 없으면 새로 생성 (정상적인 flow에서는 발생하지 않아야 함)
        console.warn('[SignupStep] auth intent_id not found in session storage, generating new one');
        return ulid();
    }, []);

    /**
     * 스텝 진입 추적
     * view() 호출 시마다 새로운 view_id 생성
     */
    const view = useCallback((params: SignupStepViewParams) => {
        const intentId = getAuthIntentId();
        track('signup_step_view', {
            intent_id: intentId,
            step_key: params.step_key,
            step_index: params.step_index,
        });

    }, [getAuthIntentId]);

    /**
     * 스텝 완료 추적
     * 마지막 view()에서 생성한 view_id 사용
     */
    const submit = useCallback((params: SignupStepSubmitParams) => {
        const intentId = getAuthIntentId();

        track('signup_step_submit', {
            intent_id: intentId,
            step_key: params.step_key,
            step_index: params.step_index,
            outcome: params.outcome,
            fail_reason: params.fail_reason,
        });

    }, [getAuthIntentId]);

    return {
        view,
        submit,
    };
}


